/**
 * Сценарии: «а если купить, внести, изменить взнос».
 *
 * Один вход для всех вопросов о последствиях — и одно жёсткое правило:
 * **сценарий ничего не меняет в рабочих данных**. Он отвечает, что будет,
 * и на этом заканчивается; записывает только человек своим действием.
 *
 * Отдельно о погашении долга. Считать его покупкой с отрицательной суммой
 * нельзя: `checkAffordability` вычитает сумму из свободных денег, и при
 * отрицательном значении остаток **растёт**, а долг не меняется вовсе.
 * Поэтому у погашения свой вид с положительной суммой и ссылкой на долг.
 */
import { ZERO, add, clampPos, formatMoney, share, sub, type Money } from './money.js';
import { dayWords, type PlainDate } from './date.js';
import {
  checkAffordability, type AffordabilityInput, type CardPurchaseEffect, type Verdict,
} from './affordability.js';
import { goalEta, monthlyNeedFor } from './goals.js';
import { annualCost, savedPerYear } from './loans.js';
import { payoffEffect } from './amortize.js';
import type { Goal, Loan } from './types.js';

/**
 * Действие, последствия которого считаем.
 *
 * Называется `ActionScenario`, чтобы не путать со `Scenario` из вердикта:
 * там это вариант месячного плана с подвинутыми целями, здесь — конкретное
 * действие человека.
 */
export type ActionScenario =
  /** `card` — покупка этой кредиткой: деньги уйдут в день платежа по выписке. */
  | { kind: 'purchase'; amount: Money; date?: PlainDate; label?: string; card?: string }
  /** Досрочный взнос: деньги уходят, долг уменьшается. */
  | { kind: 'debt_payoff'; loanId: string; amount: Money; date?: PlainDate }
  | {
    kind: 'goal_change';
    goalId: string;
    monthlyContribution?: Money;
    targetDate?: PlainDate;
    startDate?: PlainDate;
  };

export interface ScenarioEffects {
  /** Минимальный остаток по траектории — он и определяет вердикт. */
  minBalance: Money;
  minBalanceDate: PlainDate;
  /** Сколько останется сразу после действия. */
  leftAfter: Money;
  /** Насколько уменьшится долг. Только для погашения. */
  debtAfter?: Money;
  /**
   * Сколько процентов в год стоит эта сумма.
   *
   * Служит очереди гашения — она сравнивает долги между собой. Экономией не
   * является: для неё есть `interestSaved`.
   */
  savedPerYear?: Money;
  /** Сколько процентов не будет уплачено за оставшийся срок. */
  interestSaved?: Money;
  /** На сколько месяцев сократится срок. */
  monthsSaved?: number;
  /** Новая дата закрытия долга по графику. */
  payoffDate?: PlainDate;
  /** Новая дата достижения цели. Только для изменения цели. */
  goalEta?: PlainDate | null;
  /** Сколько придётся откладывать в месяц после изменения. */
  monthlyNeeded?: Money;
  /** Покупка картой: выписка, платёж, льготный срок и лимит. */
  card?: CardPurchaseEffect;
}

export interface ScenarioAlternative {
  label: string;
  date?: PlainDate;
  monthly?: Money;
  /** Что изменится, если выбрать этот вариант. */
  effect: string;
}

export interface ScenarioResult {
  scenario: ActionScenario;
  verdict: Verdict;
  effects: ScenarioEffects;
  /**
   * Чего расчёт не знает — обязательно к показу.
   *
   * Пересказ ответа не имеет права эти строки потерять: обещание «долг
   * закроется на два месяца раньше» без пересчёта графика — выдумка.
   */
  limits: string[];
  alternatives: ScenarioAlternative[];
}

export interface ScenarioInput extends AffordabilityInput {
  loans?: Loan[];
}

/**
 * Посчитать последствия действия.
 *
 * Возвращает вердикт, эффекты, ограничения и альтернативы. Рабочие данные не
 * трогает: запись — отдельное действие человека.
 */
export function runScenario(input: ScenarioInput, scenario: ActionScenario): ScenarioResult {
  switch (scenario.kind) {
    case 'purchase': return purchaseScenario(input, scenario);
    case 'debt_payoff': return payoffScenario(input, scenario);
    case 'goal_change': return goalScenario(input, scenario);
  }
}

function purchaseScenario(
  input: ScenarioInput,
  scenario: Extract<ActionScenario, { kind: 'purchase' }>,
): ScenarioResult {
  const r = checkAffordability({
    ...input,
    amount: scenario.amount,
    ...(scenario.date ? { date: scenario.date } : {}),
    ...(scenario.card ? { card: scenario.card } : {}),
  });

  const alternatives: ScenarioAlternative[] = [];
  if (r.safeDate && r.safeDate !== (scenario.date ?? input.today)) {
    alternatives.push({
      label: 'Купить позже',
      date: r.safeDate,
      effect: `К ${dayWords(r.safeDate, input.today)} покупка перестаёт быть рискованной`,
    });
  }
  if (r.monthlyToHitDate && r.monthlyToHitDate > ZERO) {
    // Покупка сегодня: копить к ней поздно, и «в месяц» было бы неправдой —
    // это вся недостающая сумма разом.
    const now = (scenario.date ?? input.today) <= input.today;
    alternatives.push(now
      ? {
        label: 'Найти недостающее',
        monthly: r.monthlyToHitDate,
        effect: `Сегодня не хватает ${formatMoney(r.monthlyToHitDate)} — без них понадобится кредит`,
      }
      : {
        label: 'Отложить к нужной дате',
        monthly: r.monthlyToHitDate,
        effect: `Откладывать ${formatMoney(r.monthlyToHitDate)} в месяц — и кредит не понадобится`,
      });
  }

  const limits: string[] = [];
  if (r.creditNeeded > ZERO) {
    limits.push(`Потребуется кредитных денег ${formatMoney(r.creditNeeded)}`);
  }
  if (r.dropsBufferBelowCritical) {
    limits.push('Подушка опустится ниже месячного порога');
  }
  // Допущения прогноза — например, неизвестный день выписки по карте, —
  // часть ответа: без них вывод выглядит точнее, чем он есть.
  limits.push(...r.limits);

  return {
    scenario,
    verdict: r.verdict,
    effects: {
      minBalance: r.minBalance,
      minBalanceDate: r.minBalanceDate,
      leftAfter: r.leftAfterPurchase,
      ...(r.card ? { card: r.card } : {}),
    },
    limits,
    alternatives,
  };
}

/**
 * Досрочный взнос по долгу.
 *
 * Деньги уходят со счетов — значит, траектория считается как у покупки той же
 * суммы. Но, в отличие от покупки, взамен уменьшается долг, и это второй
 * эффект, который человек и хочет увидеть.
 */
function payoffScenario(
  input: ScenarioInput,
  scenario: Extract<ActionScenario, { kind: 'debt_payoff' }>,
): ScenarioResult {
  const loan = (input.loans ?? []).find((l) => l.id === scenario.loanId);
  const amount = clampPos(scenario.amount);

  const r = checkAffordability({
    ...input,
    amount,
    ...(scenario.date ? { date: scenario.date } : {}),
    // Взнос на кредитку уменьшает и её будущие платежи: прогноз видит карты,
    // и без этого одни и те же деньги ушли бы дважды — сегодня и в день
    // платежа по выписке.
    ...(loan?.card ? { paysCard: loan.card.accountId } : {}),
  });

  const limits: string[] = [...r.limits];
  const effects: ScenarioEffects = {
    minBalance: r.minBalance,
    minBalanceDate: r.minBalanceDate,
    leftAfter: r.leftAfterPurchase,
  };

  if (!loan) {
    limits.push('Долг не найден: посчитана только потеря ликвидности');
  } else {
    const paid = amount < loan.principal ? amount : loan.principal;

    // Последствия считаются по графику, а не как «сумма × ставка».
    //
    // Второе — годовая стоимость этих денег, а не экономия: из неё не узнать
    // ни сокращения срока, ни непогашенных процентов. Сравниваются два
    // будущих: с взносом и без него, по одному и тому же графику.
    const effect = payoffEffect(loan, paid);
    effects.debtAfter = effect.principalAfter;
    // Неизвестную экономию не подменяем нулём: поле просто не появляется, и
    // разбор говорит о нехватке данных строкой ограничения.
    if (effect.interestSaved !== null) effects.interestSaved = effect.interestSaved;
    if (effect.monthsSaved !== null) effects.monthsSaved = effect.monthsSaved;
    if (effect.lastDate !== null) effects.payoffDate = effect.lastDate;
    // Прежнее поле остаётся для очереди гашения: она сравнивает долги между
    // собой, а не предсказывает будущее.
    effects.savedPerYear = savedPerYear(loan, paid);
    limits.push(...effect.limits);

    if (annualCost(loan) <= ZERO) {
      limits.push('Долг беспроцентный — досрочный взнос процентов не экономит');
    }
  }

  const alternatives: ScenarioAlternative[] = [];
  if (loan && amount > ZERO) {
    const half = share(amount, 0.5);
    alternatives.push({
      label: 'Внести половину',
      monthly: half,
      effect: `Экономия процентов ${formatMoney(savedPerYear(loan, half))} в год, на счетах останется больше`,
    });
  }

  return { scenario, verdict: r.verdict, effects, limits, alternatives };
}

/**
 * Изменение цели: другой взнос, другой срок, другое начало.
 *
 * Отвечает двумя числами — когда цель будет достигнута и сколько для этого
 * откладывать. Без данных о свободных деньгах дата не обещается.
 */
function goalScenario(
  input: ScenarioInput,
  scenario: Extract<ActionScenario, { kind: 'goal_change' }>,
): ScenarioResult {
  const goal = input.goals.find((g) => g.id === scenario.goalId);
  const limits: string[] = [];

  if (!goal) {
    return {
      scenario,
      verdict: 'tight',
      effects: { minBalance: ZERO, minBalanceDate: input.today, leftAfter: ZERO },
      limits: ['Цель не найдена'],
      alternatives: [],
    };
  }

  const changed: Goal = {
    ...goal,
    ...(scenario.monthlyContribution !== undefined
      ? { monthlyContribution: scenario.monthlyContribution }
      : {}),
    ...(scenario.targetDate ? { targetDate: scenario.targetDate } : {}),
    ...(scenario.startDate ? { startDate: scenario.startDate } : {}),
  };

  const monthlyNeeded = monthlyNeedFor(changed, input.today);
  const contribution = scenario.monthlyContribution ?? changed.monthlyContribution;
  const eta = goalEta(changed, contribution, input.today);

  if (contribution <= ZERO) {
    limits.push('Без взноса дата достижения неизвестна');
  }
  if (scenario.targetDate && monthlyNeeded > contribution && contribution > ZERO) {
    limits.push(
      `Чтобы успеть к ${dayWords(scenario.targetDate, input.today)}, нужно откладывать ${formatMoney(monthlyNeeded)} в месяц`,
    );
  }

  const gap = clampPos(sub(changed.targetAmount, changed.currentAmount));
  const alternatives: ScenarioAlternative[] = [];
  if (scenario.targetDate && monthlyNeeded > contribution) {
    alternatives.push({
      label: 'Сдвинуть срок',
      ...(eta ? { date: eta } : {}),
      effect: eta
        ? `При взносе ${formatMoney(contribution)} цель закрывается ${eta}`
        : 'При нулевом взносе цель не закрывается',
    });
    alternatives.push({
      label: 'Увеличить взнос',
      monthly: monthlyNeeded,
      effect: `Останется собрать ${formatMoney(gap)}`,
    });
  }

  return {
    scenario,
    // Изменение цели само по себе денег не тратит: вердикт про ликвидность
    // здесь не нужен, и «безопасно» означает лишь выполнимость.
    verdict: contribution > ZERO && monthlyNeeded <= contribution ? 'safe' : 'tight',
    effects: {
      minBalance: ZERO,
      minBalanceDate: input.today,
      leftAfter: ZERO,
      goalEta: eta,
      monthlyNeeded,
    },
    limits,
    alternatives,
  };
}

/** Сравнить несколько сценариев рядом — до трёх, как в интерфейсе. */
export function compareScenarios(
  input: ScenarioInput,
  scenarios: readonly ActionScenario[],
): ScenarioResult[] {
  return scenarios.slice(0, 3).map((s) => runScenario(input, s));
}

/** Итог сценария одной строкой — для карточки результата. */
export function scenarioSummary(r: ScenarioResult): string {
  const left = formatMoney(r.effects.leftAfter);
  const min = formatMoney(r.effects.minBalance);
  const base = r.verdict === 'safe'
    ? `Останется ${left}, минимум по траектории ${min}`
    : r.verdict === 'tight'
      ? `Останется ${left}, но минимум опускается до ${min}`
      : `Не хватит: минимум уходит до ${min}`;
  return r.limits.length > 0 ? `${base}. ${r.limits[0]}` : base;
}

export const scenarioTotal = (rs: readonly ScenarioResult[]): Money =>
  add(...rs.map((r) => r.effects.leftAfter));
